import { Directive, ElementRef, inject, Input, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';

/**
 * Directive: appFauxScrollbar
 *
 * Attaches a permanently-visible custom scrollbar alongside any scrollable element.
 * All styles are applied inline so appearance is identical across all consumers,
 * bypassing Angular's view encapsulation (programmatic DOM nodes don't receive
 * _ngcontent attributes, so component SCSS cannot reach them reliably).
 *
 * Usage:
 *   <div class="my-list" appFauxScrollbar [maxHeight]="220"> ... </div>
 */
@Directive({
    selector: '[appFauxScrollbar]',
    standalone: true
})
export class FauxScrollbarDirective implements OnInit, OnDestroy {
    @Input() maxHeight = 220;

    private readonly el = inject(ElementRef<HTMLElement>);
    private readonly renderer = inject(Renderer2);
    private readonly ngZone = inject(NgZone);

    private wrapper!: HTMLElement;
    private track!: HTMLElement;
    private thumb!: HTMLElement;
    private unlisten!: () => void;

    ngOnInit(): void {
        this._buildDom();
        this.ngZone.runOutsideAngular(() => {
            this.unlisten = this.renderer.listen(this.el.nativeElement, 'scroll', () => this._sync());
            requestAnimationFrame(() => this._sync());
        });
    }

    ngOnDestroy(): void {
        this.unlisten?.();
        this.wrapper?.replaceWith(this.el.nativeElement);
    }

    private _buildDom(): void {
        const host = this.el.nativeElement as HTMLElement;

        // Wrapper: flex row containing the scroll area + track
        this.wrapper = this.renderer.createElement('div') as HTMLElement;
        this._styles(this.wrapper, {
            position: 'relative',
            display: 'flex',
            alignItems: 'stretch',
            gap: '4px'
        });

        host.parentNode!.insertBefore(this.wrapper, host);
        this.wrapper.appendChild(host);

        // Host: hide native scrollbar, constrain height
        this._styles(host, {
            flex: '1 1 auto',
            minWidth: '0',
            overflowY: 'scroll',
            maxHeight: `${this.maxHeight}px`
        });
        this.renderer.setStyle(host, 'scrollbar-width', 'none');
        // Hide WebKit native scrollbar
        const styleTag = this.renderer.createElement('style') as HTMLStyleElement;
        styleTag.textContent = `[data-faux-scroll-id="${this._id}"]::-webkit-scrollbar { display: none; }`;
        host.setAttribute('data-faux-scroll-id', this._id);
        document.head.appendChild(styleTag);

        // Track
        this.track = this.renderer.createElement('div') as HTMLElement;
        this._styles(this.track, {
            position: 'relative',
            width: '3px',
            flex: '0 0 3px',
            background: 'rgba(0, 0, 0, 0.03)',
            borderRadius: '1px',
            alignSelf: 'stretch',
            margin: '2px 0',
            overflow: 'hidden'
        });

        // Thumb
        this.thumb = this.renderer.createElement('div') as HTMLElement;
        this._styles(this.thumb, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            background: 'rgba(0, 0, 0, 0.08)',
            borderRadius: '1px',
            willChange: 'transform',
            opacity: '0'
        });

        this.track.appendChild(this.thumb);
        this.wrapper.appendChild(this.track);
    }

    private _sync(): void {
        const host = this.el.nativeElement as HTMLElement;
        const { scrollHeight, clientHeight, scrollTop } = host;
        const isScrollable = scrollHeight > clientHeight;
        const thumbHeight = isScrollable ? Math.max(24, (clientHeight / scrollHeight) * clientHeight) : 0;
        const thumbTop = isScrollable ? (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - thumbHeight) : 0;

        this.thumb.style.height = `${thumbHeight}px`;
        this.thumb.style.transform = `translateY(${thumbTop}px)`;
        this.thumb.style.opacity = isScrollable ? '1' : '0';
    }

    private _styles(el: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
        Object.entries(styles).forEach(([prop, val]) => this.renderer.setStyle(el, prop, val));
    }

    private readonly _id = Math.random().toString(36).slice(2);
}
